import { generatePath, useNavigate, useParams } from "react-router-dom";
import { ProjectRoleForm } from "@/modules/projects/components/roles/ProjectRoleForm";
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import {
  Module,
  PATH_PROJECT_ROLES,
  PATH_PROJECTS,
  PATH_ROOT,
} from "@/constants";
import { useMemo } from "react";
import { BreadcrumbItem } from "@/types/breadcrumb";
import { generateDisplayName } from "@/modules/projects/lib/generateDisplayName";
import { UserPen } from "lucide-react";
import { ApiError } from "@/services/api";
import { ReadProjectRole, WriteProjectRole } from "@/types/projectRoleType";
import { ProjectRolesService } from "@/modules/projects/service/projectRole";
import { toast } from "@/components/ui/use-toast";
import { exists } from "i18next";
import Spinner from "@/components/Spinner";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";

export const ProjectRoleEditor = () => {
  const { projectId, roleId } = useParams() as {
    projectId: string;
    roleId: string;
  };
  const { t } = useTranslation();
  const { can_update } = useModulePermissions();
  const title = t("projectRoles.title.edit");

  // In role request return we have no `client` value
  // needed for the breadcrumb, so we need to get it from the project
  const projectQuery = useProjectQuery(projectId);

  const breadcrumbItems = useMemo(() => {
    const breadcrumbItems: BreadcrumbItem[] = [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("projects.breadcrumb.projects"), href: PATH_PROJECTS },
    ];

    breadcrumbItems.push({
      label: t("projects.breadcrumb.project", {
        name:
          projectQuery.data !== undefined
            ? generateDisplayName(projectQuery.data)
            : "...",
      }),
      href: `${PATH_PROJECTS}/${projectId}`,
    });

    breadcrumbItems.push({
      label: t(`projects.breadcrumb.roles`),
      href: `${PATH_PROJECTS}/${projectId}/${Module.ProjectRoles}`,
    });

    breadcrumbItems.push({
      label: title,
    });
    return breadcrumbItems;
  }, [t, projectId, projectQuery.data, title]);

  const roleQuery = useQuery({
    queryKey: [Module.ProjectRoles, projectId, roleId],
    queryFn: () => ProjectRolesService.get(projectId, roleId),
  });

  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.ProjectRoles,
  );

  const editProjectRoleMutationFn: MutationFunction<
    ReadProjectRole,
    WriteProjectRole
  > = async (values) => ProjectRolesService.patch(projectId, roleId, values);

  const navigate = useNavigate();

  const editProjectRoleMutation = useMutation<
    ReadProjectRole,
    ApiError,
    WriteProjectRole
  >({
    mutationFn: editProjectRoleMutationFn,
    onSuccess: (response) => {
      updateQueryDataForExistingItem(response);
      toast({
        title: t("projectRoles.success.update"),
      });
      navigate(generatePath(PATH_PROJECT_ROLES, { projectId }));
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("projectRoles.errors.update"),
        description,
        variant: "destructive",
      });
    },
  });

  const isFetching = useMemo(() => {
    return (
      projectQuery.isFetching ||
      roleQuery.isFetching ||
      editProjectRoleMutation.isPending
    );
  }, [
    projectQuery.isFetching,
    roleQuery.isFetching,
    editProjectRoleMutation.isPending,
  ]);

  if (roleQuery.isLoading) {
    return (
      <Spinner label={t("projectRoles.loading")} className="w-full h-full " />
    );
  }

  return (
    <div className="space-y-2">
      <PageHeader breadcrumbItems={breadcrumbItems} title={title} />
      <ProjectDetailsAndNavigation />
      <ProjectRoleForm
        onSubmit={editProjectRoleMutation.mutate}
        disabled={isFetching || !can_update}
        data={roleQuery.data}
        icon={<UserPen className="w-6 h-6 mr-3" />}
        title={title}
      />
    </div>
  );
};
